import { useState } from 'react';
import { Link } from 'react-router-dom';
import { partI, partII, partIII, partIV } from './data/geometryRegentsNY';
import PartIMCQ from './components/PartIMCQ';
import OpenEndedQuestion from './components/OpenEndedQuestion';
import RegentsTestCard from './components/RegentsTestCard';

type ActivePart = 'I' | 'II' | 'III' | 'IV';

const partInfo = [
  {
    part: 'I' as ActivePart,
    label: 'Part I',
    subtitle: 'Multiple Choice',
    questions: 24,
    points: 48,
    time: '~40 min',
    color: 'from-teal-500 to-emerald-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-700',
    icon: 'ri-checkbox-multiple-line',
    desc: '24 multiple-choice questions, 2 points each. No partial credit.',
  },
  {
    part: 'II' as ActivePart,
    label: 'Part II',
    subtitle: 'Short Answer',
    questions: 7,
    points: 14,
    time: '~25 min',
    color: 'from-teal-600 to-cyan-600',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-700',
    icon: 'ri-edit-2-line',
    desc: '7 open-ended questions, 2 points each. Show all work for full credit.',
  },
  {
    part: 'III' as ActivePart,
    label: 'Part III',
    subtitle: 'Extended Response',
    questions: 3,
    points: 12,
    time: '~30 min',
    color: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: 'ri-file-text-line',
    desc: '3 extended-response questions, 4 points each. Proofs and multi-step problems.',
  },
  {
    part: 'IV' as ActivePart,
    label: 'Part IV',
    subtitle: 'Extended Response',
    questions: 1,
    points: 6,
    time: '~20 min',
    color: 'from-rose-500 to-pink-600',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-700',
    icon: 'ri-trophy-line',
    desc: '1 extended-response question, 6 points. Multi-part real-world modeling problem.',
  },
];

export default function GeometryRegentsPage() {
  const [activePart, setActivePart] = useState<ActivePart | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [partISubmitted, setPartISubmitted] = useState(false);
  const [partIFilter, setPartIFilter] = useState<'all' | 'correct' | 'incorrect'>('all');

  const handleSelect = (qId: number, optIndex: number) => {
    if (partISubmitted) return;
    setSelectedAnswers(prev => ({ ...prev, [qId]: optIndex }));
  };

  const handlePartISubmit = () => {
    setPartISubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePartIReset = () => {
    setSelectedAnswers({});
    setPartISubmitted(false);
    setPartIFilter('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const partIScore = partI.reduce((acc, q) => acc + (selectedAnswers[q.id] === q.correctAnswer ? 1 : 0), 0);
  const partIPoints = partIScore * 2;
  const partIPercent = Math.round((partIScore / partI.length) * 100);
  const partIAnswered = Object.keys(selectedAnswers).length;

  const filteredPartI = partI.filter(q => {
    if (partIFilter === 'all') return true;
    if (partIFilter === 'correct') return partISubmitted && selectedAnswers[q.id] === q.correctAnswer;
    if (partIFilter === 'incorrect') return partISubmitted && selectedAnswers[q.id] !== undefined && selectedAnswers[q.id] !== q.correctAnswer;
    return true;
  });

  const getScoreGradient = () => {
    if (partIPercent >= 85) return 'from-emerald-500 to-teal-600';
    if (partIPercent >= 65) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-600';
  };

  const getScoreMsg = () => {
    if (partIPercent >= 85) return 'Excellent! You are Regents-ready!';
    if (partIPercent >= 65) return 'Good work — review the missed questions.';
    if (partIPercent >= 50) return 'Keep practicing — you are improving!';
    return 'Review the lessons and try again. You can do it!';
  };

  // ── Landing Page ──────────────────────────────────────────────────────────
  if (!activePart) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        {/* Navbar */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                <i className="ri-graduation-cap-fill text-white text-xl"></i>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900 leading-tight">Mr. Augustine's Math Academy</p>
                <p className="text-xs text-emerald-600 font-medium">NY Geometry Regents — Full Practice Exam</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/grades/geometry-tests" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm whitespace-nowrap">
                <i className="ri-map-pin-line mr-1"></i>State Tests
              </Link>
              <Link to="/grades/grade-10" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm whitespace-nowrap">
                <i className="ri-book-open-line mr-1"></i>Geometry Lessons
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <div className="bg-gradient-to-r from-emerald-900 via-teal-800 to-emerald-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-5">
                  <span className="text-xl">🗽</span>
                  New York State Regents Examination — Geometry
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                  NY Geometry Regents<br />
                  <span className="text-amber-300">Full Practice Exam</span>
                </h1>
                <p className="text-emerald-100 text-lg leading-relaxed mb-6 max-w-xl">
                  The most authentic NY Regents Geometry practice available — all four parts, open-ended
                  questions with full rubrics, step-by-step solutions, and real exam-style extended responses.
                </p>
                <div className="flex flex-wrap gap-3 mb-8">
                  {[
                    { icon: 'ri-question-line', label: '35 Total Questions' },
                    { icon: 'ri-award-line', label: '86 Total Points' },
                    { icon: 'ri-time-line', label: '~3 Hours (Full Exam)' },
                    { icon: 'ri-file-text-line', label: 'Parts I, II, III & IV' },
                    { icon: 'ri-lightbulb-line', label: 'Full Rubrics Included' },
                  ].map((b, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full text-sm font-medium">
                      <i className={`${b.icon} text-amber-300`}></i>
                      {b.label}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => setActivePart('I')}
                    className="px-8 py-4 bg-amber-400 text-gray-900 rounded-xl font-black text-lg hover:bg-amber-300 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-fill mr-2"></i>Start Part I — Multiple Choice
                  </button>
                  <Link
                    to="/grades/geometry-regents-open"
                    className="px-8 py-4 bg-white/20 hover:bg-white/30 text-white rounded-xl font-black text-lg transition-colors shadow-lg whitespace-nowrap border-2 border-white/40"
                  >
                    <i className="ri-file-list-3-line mr-2"></i>NY Regents Open Exam
                  </Link>
                </div>
              </div>

              {/* Score Card Preview */}
              <div className="flex-shrink-0 bg-white/10 backdrop-blur rounded-2xl border border-white/20 p-6 w-full max-w-sm">
                <h3 className="text-lg font-bold mb-4 text-center">Exam Structure</h3>
                <div className="space-y-3">
                  {partInfo.map(p => (
                    <div key={p.part} className="flex items-center justify-between bg-white/10 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${p.color}`}>
                          <i className={`${p.icon} text-white text-sm`}></i>
                        </div>
                        <div>
                          <p className="font-bold text-sm">{p.label}</p>
                          <p className="text-white/70 text-xs">{p.subtitle}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm">{p.points} pts</p>
                        <p className="text-white/70 text-xs">{p.questions} Qs</p>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-white/20 pt-3 flex justify-between items-center">
                    <span className="font-bold text-white">Total</span>
                    <span className="font-black text-amber-300 text-lg">86 Points</span>
                  </div>
                </div>
                <p className="text-center text-white/60 text-xs mt-4">
                  Passing score: 65 points (75%) — Mastery: 75+ points (87%)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Teacher Message */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex items-start gap-5">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-smile-fill text-white text-2xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">From Mr. Augustine</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                "The NY Geometry Regents is a four-part exam. Part I is multiple choice — fast and focused.
                Parts II, III, and IV are open-ended — and this is where students lose the most points by not showing their work.
                On this practice exam, I have included full rubrics for every open-ended question so you know exactly
                what the graders are looking for. Write every step. State every theorem. Show every calculation.
                That is how you earn full credit — and that is how you pass."
              </p>
              <p className="text-emerald-600 font-bold text-sm mt-2">— Mr. Augustine Anyimadu-Ahenkae · Former NYC Public High School Math Teacher · 25+ Years</p>
            </div>
          </div>
        </div>

        {/* Part Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Practice Tests — Choose Your Exam</h2>
          <p className="text-gray-500 text-sm mb-8">Three full-length practice tests with unique questions. Each test includes all four parts with complete solutions and scoring rubrics.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <RegentsTestCard
              testNumber={1}
              title="Test 1"
              description="Full authentic NY Regents exam — 35 questions covering transformations, proofs, similarity, circles, and 3D geometry."
              gradient="from-emerald-500 to-teal-600"
              bgColor="bg-emerald-50"
              borderColor="border-emerald-200"
              route="/grades/geometry-regents"
            />
            <RegentsTestCard
              testNumber={2}
              title="Test 2"
              description="35 brand-new questions with focus on coordinate geometry, trigonometry, and composite solids."
              gradient="from-violet-500 to-purple-600"
              bgColor="bg-violet-50"
              borderColor="border-violet-200"
              route="/grades/geometry-regents-test-2"
            />
            <RegentsTestCard
              testNumber={3}
              title="Test 3"
              description="35 fresh questions emphasizing proofs, angle relationships, and real-world modeling applications."
              gradient="from-indigo-500 to-blue-600"
              bgColor="bg-indigo-50"
              borderColor="border-indigo-200"
              route="/grades/geometry-regents-test-3"
            />
          </div>

          {/* Scoring Guide Section */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-xl p-8 text-white mb-8">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <i className="ri-award-line text-4xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-black mb-3">NY Regents Scoring Guide</h3>
                <p className="text-amber-100 text-sm leading-relaxed mb-4">
                  Understanding how your raw score converts to a scaled score is crucial for exam preparation.
                  The NY Regents uses a conversion chart that adjusts for exam difficulty.
                </p>
                
                <div className="bg-white/10 rounded-xl p-5 mb-4">
                  <h4 className="font-bold text-lg mb-3">Raw Score → Scaled Score Conversion</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="text-2xl font-black">86</div>
                      <div className="text-amber-100 text-xs">Raw → 100 Scaled</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="text-2xl font-black">73</div>
                      <div className="text-amber-100 text-xs">Raw → 85 Scaled</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="text-2xl font-black">57</div>
                      <div className="text-amber-100 text-xs">Raw → 65 Scaled (Pass)</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="text-2xl font-black">30</div>
                      <div className="text-amber-100 text-xs">Raw → 55 Scaled</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-emerald-500/30 rounded-xl p-4 border-2 border-emerald-300">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-trophy-fill text-2xl"></i>
                      <span className="font-bold">Mastery</span>
                    </div>
                    <div className="text-sm text-emerald-100">
                      <strong>85-100:</strong> College-ready performance. Demonstrates deep understanding of all geometry concepts.
                    </div>
                  </div>
                  <div className="bg-amber-500/30 rounded-xl p-4 border-2 border-amber-300">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-checkbox-circle-fill text-2xl"></i>
                      <span className="font-bold">Passing</span>
                    </div>
                    <div className="text-sm text-amber-100">
                      <strong>65-84:</strong> Meets graduation requirements. Solid grasp of core geometry standards.
                    </div>
                  </div>
                  <div className="bg-red-500/30 rounded-xl p-4 border-2 border-red-300">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-error-warning-fill text-2xl"></i>
                      <span className="font-bold">Below Passing</span>
                    </div>
                    <div className="text-sm text-red-100">
                      <strong>0-64:</strong> Additional review needed. Focus on weak areas and retake.
                    </div>
                  </div>
                </div>

                <div className="bg-white/10 rounded-xl p-4">
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    <i className="ri-lightbulb-fill"></i>
                    Scoring Strategy Tips
                  </h4>
                  <ul className="space-y-1 text-sm text-amber-100">
                    <li>• <strong>Part I:</strong> Each question = 2 points. No partial credit. Answer all 24 even if you guess.</li>
                    <li>• <strong>Parts II-IV:</strong> Partial credit available. Always show your work — even wrong answers can earn points.</li>
                    <li>• <strong>Target Score:</strong> Aim for 60+ raw points (70%) to ensure passing after scaling.</li>
                    <li>• <strong>Time Management:</strong> Spend ~40 min on Part I, save 80+ min for open-ended questions.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Original Part Cards for Test 1 */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2 mt-12">Test 1 — Practice by Part</h2>
          <p className="text-gray-500 text-sm mb-8">You can practice each part of Test 1 independently. All parts include complete solutions and scoring rubrics.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {partInfo.map(p => (
              <div key={p.part} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group">
                <div className={`bg-gradient-to-r ${p.color} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <i className={`${p.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <p className="text-white/80 text-xs font-medium uppercase tracking-wide">{p.subtitle}</p>
                        <p className="text-2xl font-black">{p.label}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black">{p.points}</p>
                      <p className="text-white/80 text-xs">points</p>
                    </div>
                  </div>
                  <p className="text-white/80 text-sm">{p.desc}</p>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-5 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <i className="ri-question-line text-emerald-500"></i>
                      <span>{p.questions} question{p.questions > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line text-emerald-500"></i>
                      <span>{p.time}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-award-line text-amber-500"></i>
                      <span>{p.points} pts</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setActivePart(p.part)}
                    className={`w-full py-3 bg-gradient-to-r ${p.color} text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity shadow-md whitespace-nowrap cursor-pointer`}
                  >
                    <i className="ri-play-circle-fill mr-2"></i>
                    Start {p.label}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* NY Regents Open Exam Banner */}
          <div className="mt-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0 w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
                <i className="ri-file-list-3-line text-4xl"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-black mb-2">NY Regents Open-Ended Exam</h3>
                <p className="text-emerald-100 text-sm leading-relaxed">
                  Full authentic NY Regents Geometry exam with all four parts (I–IV) — 35 questions, 86 points total. 
                  Includes multiple choice, short answer, extended response, and comprehensive problems with full rubrics and sample answers.
                </p>
              </div>
              <Link
                to="/grades/geometry-regents-open"
                className="px-8 py-4 bg-white text-emerald-700 rounded-xl font-black text-lg hover:bg-emerald-50 transition-colors shadow-lg whitespace-nowrap"
              >
                <i className="ri-arrow-right-line mr-2"></i>Start Full Exam
              </Link>
            </div>
          </div>
        </div>

        {/* Regents Tips */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-gradient-to-r from-emerald-700 to-teal-700 rounded-2xl p-8 text-white">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <i className="ri-lightbulb-fill text-amber-300 text-2xl"></i>
              NY Regents Geometry — Exam Strategy Tips
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: 'ri-pencil-line', tip: 'Part I: Eliminate wrong answers first. If unsure, make your best guess — no penalty for wrong answers.' },
                { icon: 'ri-file-text-line', tip: 'Parts II–IV: Always show your work. A correct answer with no work shown may receive 0 points.' },
                { icon: 'ri-shapes-line', tip: 'Draw and label a diagram for every geometry problem, even if one is provided.' },
                { icon: 'ri-book-2-line', tip: 'State the theorem or formula you are using before applying it — graders reward this.' },
                { icon: 'ri-check-double-line', tip: 'Check your answers by substituting back into the original equation or verifying with a different method.' },
                { icon: 'ri-time-line', tip: 'Budget your time: ~40 min for Part I, ~25 min for Part II, ~30 min for Part III, ~20 min for Part IV.' },
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/10 rounded-xl p-4">
                  <i className={`${t.icon} text-amber-300 text-lg flex-shrink-0 mt-0.5`}></i>
                  <p className="text-sm text-emerald-100 leading-relaxed">{t.tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-fill text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-white">Mr. Augustine's Math Academy</h3>
            </div>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">Empowering every student to master mathematics through proven teaching methods and rigorous practice.</p>
            <p className="text-xs text-gray-500 mt-4">© 2024 Mr. Augustine's Math Academy. All rights reserved.</p>
          </div>
        </footer>
      </div>
    );
  }

  // ── Part I View ───────────────────────────────────────────────────────────
  if (activePart === 'I') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="bg-gradient-to-r from-teal-600 to-emerald-700 text-white sticky top-0 z-40 shadow-lg">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
                <i className="ri-checkbox-multiple-fill text-xl"></i>
              </div>
              <div>
                <h1 className="text-lg font-bold">Part I — Multiple Choice</h1>
                <p className="text-white/80 text-xs">24 questions · 48 points · NY Geometry Regents</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!partISubmitted && (
                <span className="text-sm text-white/80 whitespace-nowrap">{partIAnswered}/24 answered</span>
              )}
              <button onClick={() => setActivePart(null)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line mr-1"></i>Back
              </button>
            </div>
          </div>
          {!partISubmitted && (
            <div className="max-w-5xl mx-auto px-4 pb-3">
              <div className="w-full bg-white/20 rounded-full h-1.5">
                <div className="bg-white rounded-full h-1.5 transition-all duration-300" style={{ width: `${(partIAnswered / 24) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Score Summary */}
          {partISubmitted && (
            <div className={`bg-gradient-to-r ${getScoreGradient()} rounded-2xl p-8 text-white mb-8 shadow-xl`}>
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="text-center">
                  <div className="text-7xl font-black">{partIPercent}%</div>
                  <div className="text-white/80 text-sm mt-1">Part I Score</div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2">{getScoreMsg()}</h2>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold">{partIScore}</div>
                      <div className="text-xs text-white/80">Correct</div>
                    </div>
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold">{24 - partIScore}</div>
                      <div className="text-xs text-white/80">Incorrect</div>
                    </div>
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold">{partIPoints}/48</div>
                      <div className="text-xs text-white/80">Points</div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3 flex-wrap">
                    <button onClick={handlePartIReset} className="px-5 py-2 bg-white text-gray-800 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-refresh-line mr-1"></i>Retake Part I
                    </button>
                    <button onClick={() => setActivePart('II')} className="px-5 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-bold text-sm transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-arrow-right-line mr-1"></i>Continue to Part II
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filter Tabs */}
          {partISubmitted && (
            <div className="flex gap-2 mb-6 flex-wrap">
              {(['all', 'correct', 'incorrect'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setPartIFilter(f)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    partIFilter === f
                      ? f === 'correct' ? 'bg-emerald-600 text-white' : f === 'incorrect' ? 'bg-red-500 text-white' : 'bg-teal-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {f === 'all' && `All (24)`}
                  {f === 'correct' && `Correct (${partIScore})`}
                  {f === 'incorrect' && `Incorrect (${24 - partIScore})`}
                </button>
              ))}
            </div>
          )}

          <PartIMCQ
            questions={filteredPartI}
            submitted={partISubmitted}
            selectedAnswers={selectedAnswers}
            onSelect={handleSelect}
          />

          {!partISubmitted && (
            <div className="mt-10 flex flex-col items-center gap-4">
              <p className="text-sm text-gray-500">
                {partIAnswered < 24 ? `${24 - partIAnswered} question(s) unanswered` : 'All questions answered — ready to submit!'}
              </p>
              <button
                onClick={handlePartISubmit}
                disabled={partIAnswered === 0}
                className={`px-10 py-4 rounded-xl font-bold text-lg shadow-lg transition-all whitespace-nowrap cursor-pointer ${
                  partIAnswered > 0
                    ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-send-plane-fill mr-2"></i>Submit Part I ({partIAnswered}/24)
              </button>
            </div>
          )}

          {partISubmitted && (
            <div className="mt-10 flex justify-center gap-4 flex-wrap">
              <button onClick={handlePartIReset} className="px-8 py-3 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-refresh-line mr-2"></i>Retake Part I
              </button>
              <button onClick={() => setActivePart('II')} className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-right-line mr-2"></i>Continue to Part II
              </button>
              <button onClick={() => setActivePart(null)} className="px-8 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line mr-2"></i>Back to Exam Overview
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Parts II, III, IV View ────────────────────────────────────────────────
  const openPartData = activePart === 'II' ? partII : activePart === 'III' ? partIII : partIV;
  const currentPartInfo = partInfo.find(p => p.part === activePart)!;
  const nextPart: Record<string, ActivePart | null> = { II: 'III', III: 'IV', IV: null };
  const prevPart: Record<string, ActivePart> = { II: 'I', III: 'II', IV: 'III' };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentPartInfo.color} text-white sticky top-0 z-40 shadow-lg`}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
              <i className={`${currentPartInfo.icon} text-xl`}></i>
            </div>
            <div>
              <h1 className="text-lg font-bold">{currentPartInfo.label} — {currentPartInfo.subtitle}</h1>
              <p className="text-white/80 text-xs">
                {currentPartInfo.questions} question{currentPartInfo.questions > 1 ? 's' : ''} · {currentPartInfo.points} points · NY Geometry Regents
              </p>
            </div>
          </div>
          <button onClick={() => setActivePart(null)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line mr-1"></i>Back
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Instructions Banner */}
        <div className={`${currentPartInfo.bg} border ${currentPartInfo.border} rounded-2xl p-5 mb-8 flex items-start gap-4`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${currentPartInfo.color} flex-shrink-0`}>
            <i className="ri-information-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className={`font-bold ${currentPartInfo.text} mb-1`}>{currentPartInfo.label} Instructions</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              {activePart === 'II' && 'Answer all 7 questions in this part. Each correct answer will receive 2 credits. Clearly indicate the necessary steps, including appropriate formula substitutions, diagrams, graphs, charts, etc. Utilize the information provided for each question to determine your answer.'}
              {activePart === 'III' && 'Answer all 3 questions in this part. Each correct answer will receive 4 credits. Clearly indicate the necessary steps, including appropriate formula substitutions, diagrams, graphs, charts, etc. For all questions in this part, a correct numerical answer with no work shown will receive only 1 credit.'}
              {activePart === 'IV' && 'Answer the question in this part. A correct answer will receive 6 credits. Clearly indicate the necessary steps, including appropriate formula substitutions, diagrams, graphs, charts, etc. A correct numerical answer with no work shown will receive only 1 credit.'}
            </p>
          </div>
        </div>

        {/* Open-Ended Questions */}
        <div className="space-y-8">
          {openPartData.map((q, idx) => (
            <OpenEndedQuestion
              key={q.id}
              question={q}
              qNumber={idx + 1}
              submitted={false}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="mt-12 flex flex-wrap justify-between items-center gap-4">
          <button
            onClick={() => setActivePart(prevPart[activePart])}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>Back to {currentPartInfo.label === 'Part II' ? 'Part I' : currentPartInfo.label === 'Part III' ? 'Part II' : 'Part III'}
          </button>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => setActivePart(null)} className="px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-home-line mr-2"></i>Exam Overview
            </button>
            {nextPart[activePart] && (
              <button
                onClick={() => setActivePart(nextPart[activePart] as ActivePart)}
                className={`px-6 py-3 bg-gradient-to-r ${currentPartInfo.color} text-white rounded-xl font-bold hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer`}
              >
                Continue to {activePart === 'II' ? 'Part III' : 'Part IV'}<i className="ri-arrow-right-line ml-2"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
